import { useEffect, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { IDECON_WEBSITE_URL, REDIRECT_DELAY_SECONDS, redirectToIdeconWebsite } from '../constants';

type SurveyResultProps = {
  tone: 'success' | 'error';
  title: string;
  message: string;
  secondsUntilRedirect: number;
};

const SurveyResult = ({ tone, title, message, secondsUntilRedirect }: SurveyResultProps) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isSuccess = tone === 'success';

  useEffect(() => {
    titleRef.current?.focus();
  }, [title]);

  return (
    <Box sx={{ px: { xs: 2.5, sm: 6 }, py: { xs: 6, sm: 8 }, textAlign: 'center' }}>
      <Box role={isSuccess ? 'status' : 'alert'} aria-live={isSuccess ? 'polite' : 'assertive'}>
        <Box
          sx={(theme) => ({
            display: 'grid',
            width: 76,
            height: 76,
            mx: 'auto',
            mb: 2.5,
            placeItems: 'center',
            borderRadius: '50%',
            color: isSuccess ? 'success.main' : 'error.main',
            backgroundColor: alpha(isSuccess ? theme.palette.success.main : theme.palette.error.main, 0.12),
            '& svg': { fontSize: 42 },
          })}>
          {isSuccess ? (
            <CheckCircleOutlineRoundedIcon aria-hidden="true" />
          ) : (
            <ErrorOutlineRoundedIcon aria-hidden="true" />
          )}
        </Box>

        <Typography ref={titleRef} tabIndex={-1} component="h1" variant="h4" sx={{ mb: 1.25, outline: 'none' }}>
          {title}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.7 }}>
          {message}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 360, mx: 'auto', mt: 4 }}>
        <Typography
          role="status"
          aria-live="polite"
          aria-atomic="true"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.25 }}>
          <FormattedMessage
            id="ticketSurvey.redirectMessage"
            defaultMessage="{seconds} saniye içinde Idecon web sitesine yönlendirileceksiniz."
            values={{ seconds: secondsUntilRedirect }}
          />
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(secondsUntilRedirect / REDIRECT_DELAY_SECONDS) * 100}
          aria-hidden="true"
          sx={{ height: 5, mb: 3, borderRadius: 99 }}
        />
        <Button
          component="a"
          href={IDECON_WEBSITE_URL}
          onClick={(event) => {
            event.preventDefault();
            redirectToIdeconWebsite();
          }}
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          fullWidth>
          <FormattedMessage id="ticketSurvey.returnButton" defaultMessage="Idecon web sitesine git" />
        </Button>
      </Box>
    </Box>
  );
};

export default SurveyResult;
